import { isNotUndefined } from "@faire/typescript";
import deleteMessageApi from "@faire/web-api--source/endpoints/www/api/messenger/messageToken/delete";
import createConversationV3Action from "@faire/web-api--source/endpoints/www/api/v3/messenger/create-conversation/post";
import { useMutation as useListMessagesPageMutation } from "@faire/web-api--source/endpoints/www/api/v3/messenger/list-messages-page/post-hooks";
import getMessengerMessagesV3 from "@faire/web-api--source/endpoints/www/api/v3/messenger/list-messages/post";
import sendMessageReadReceiptV3 from "@faire/web-api--source/endpoints/www/api/v3/messenger/read-conversation/post";
import sendMessengerMessageV3 from "@faire/web-api--source/endpoints/www/api/v3/messenger/send-message/post";
import { ICreateConversationRequestV3 } from "@faire/web-api--source/faire/messenger/ICreateConversationRequestV3";
import { IListMessagesPageRequest } from "@faire/web-api--source/faire/messenger/IListMessagesPageRequest";
import { IListMessagesRequestV3 } from "@faire/web-api--source/faire/messenger/IListMessagesRequestV3";
import { IMessageContents } from "@faire/web-api--source/faire/messenger/IMessageContents";
import { IMessengerParticipantToken } from "@faire/web-api--source/faire/messenger/IMessengerParticipantToken";
import { ISendMessengerMessageRequestV3 } from "@faire/web-api--source/faire/messenger/ISendMessengerMessageRequestV3";
import { IBasicUser } from "@faire/web-api--source/indigofair/data/IBasicUser";
import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { IInlineMessage } from "@faire/web-api--source/indigofair/data/IInlineMessage";
import { IMessengerConversation } from "@faire/web-api--source/indigofair/data/IMessengerConversation";
import { IMessengerConversationParticipant } from "@faire/web-api--source/indigofair/data/IMessengerConversationParticipant";
import { IMessengerMessage } from "@faire/web-api--source/indigofair/data/IMessengerMessage";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { MessengerConversationType } from "@faire/web-api--source/indigofair/data/MessengerConversationType";
import { UserRole } from "@faire/web-api--source/indigofair/data/UserRole";
import { getWebApiErrorMessage } from "@faire/web/common/getErrorMessage";
import { localDate } from "@faire/web/common/localization/localDate";
import {
  backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";
import { logError } from "@faire/web/common/logging";
import { startOfDay } from "date-fns/startOfDay";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import { useCallback } from "react";

import { useSettingEnableMessengerInlineContext } from "../../settings/useSettingEnableMessengerInlineContext";

import {
  SYNTHETIC_BRAND_VACATION_TOKEN,
  SYNTHETIC_OTHER_PARTY_STATUS_TOKEN,
  SYNTHETIC_SUPPORT_MESSAGE,
  SYNTHETIC_SUPPORT_TOKEN,
} from "../constants";

import {
  createNewMessage,
  updateMessage,
  setError,
  getToken,
  getCreatedAt,
  uploadImages,
  isDeleted,
  MessageModel,
} from "./MessageModel";
import { throttlePromise } from "./throttlePromise";

// Convert from class to interface
export interface ConversationModel {
  otherParty?: IUser;
  data: IMessengerConversation;
  messages: Map<string, MessageModel>;
  inlineMessages: Map<string, IInlineMessage>;
  user: IUser;
  messageAuthors: Array<IBasicUser>;
  otherPartyCompany?: string;
  isFirstFetch: boolean;
  nextRequest?: IListMessagesRequestV3;
  isLoading: boolean;
  brand?: IGetMessengerConversationsResponse.ISimpleBrand;
  retailer?: IGetMessengerConversationsResponse.ISimpleRetailer;
  needsReply: boolean;
}

// Until we've migrated all clients to be using the
// `IListMessagesPageRequest` we need to support a union of the
// request tokens for both.
interface INextRequest extends IListMessagesRequestV3 {
  pagination_tokens?: IListMessagesPageRequest.IPaginationTokens;
}

/**
 * Factory function to create a new ConversationModel instance
 */
export const createConversationModel = (
  conversationData: Partial<IMessengerConversation>,
  strictLocalize: StrictLocalizeFunction,
  user?: IUser,
  brand?: IGetMessengerConversationsResponse.ISimpleBrand,
  retailer?: IGetMessengerConversationsResponse.ISimpleRetailer
): ConversationModel => {
  if (!user) {
    throw new Error(
      "By the time a conversation can be created, there should be a user - Ian"
    );
  }

  // Create initial model
  let conversation: ConversationModel = {
    user,
    brand,
    retailer,
    data: IMessengerConversation.build({}),
    messages: new Map<string, MessageModel>(),
    inlineMessages: new Map<string, IInlineMessage>(),
    messageAuthors: [],
    isFirstFetch: true,
    isLoading: false,
    needsReply: false,
  };

  // Update with conversation data
  conversation = updateConversation(conversation, conversationData);

  // Create synthetic events
  conversation = createSyntheticBrandVacationMessage(
    conversation,
    strictLocalize
  );
  conversation = createSyntheticSupportMessage(
    conversation,
    undefined,
    strictLocalize
  );

  // Need to handle reactions differently since we're now using immutable updates
  // Return the conversation with reactions set up
  return conversation;
};

const isOnVacation = (
  brand?: IGetMessengerConversationsResponse.ISimpleBrand
): boolean => {
  const now = Date.now();

  return (
    !!brand &&
    !!brand.vacation_start_date &&
    !!brand.vacation_end_date &&
    brand.vacation_start_date <= now &&
    brand.vacation_end_date >= now
  );
};

// Standalone functions - updated to use immutable pattern

export const updateConversation = (
  conversation: ConversationModel,
  conversationData: Partial<IMessengerConversation>,
  simple_brand?: IGetMessengerConversationsResponse.ISimpleBrand,
  simple_retailer?: IGetMessengerConversationsResponse.ISimpleRetailer
): ConversationModel => {
  if (isEmpty(conversationData)) {
    return conversation;
  }

  // Create updated data object
  const updatedData = {
    ...conversation.data,
    ...conversationData,
  };

  // Handle other party separately
  const otherParty = conversationData.other_party
    ? IUser.build(conversationData.other_party)
    : conversation.otherParty;

  if (conversationData.other_party && !conversationData.other_party.token) {
    throw new Error("a conversation user should always be valid - Ian");
  }

  // Delete other_party, it's passed as the `otherParty` argument
  if (updatedData.other_party) {
    delete updatedData.other_party;
  }

  let updatedConversation: ConversationModel = {
    ...conversation,
    data: updatedData,
    otherParty,
    brand: simple_brand ?? conversation.brand,
    retailer: simple_retailer ?? conversation.retailer,
    otherPartyCompany:
      conversationData.other_party_company ?? conversation.otherPartyCompany,
    needsReply: conversationData.needs_reply ?? conversation.needsReply,
  };

  if (conversationData.latest_message) {
    updatedConversation = createOrUpdateMessage(
      updatedConversation,
      conversationData.latest_message
    );
  }

  return updatedConversation;
};

export const setIsStarredByBrand = (
  conversation: ConversationModel,
  isStarred: boolean
): ConversationModel => {
  return {
    ...conversation,
    data: {
      ...conversation.data,
      is_starred_by_brand: isStarred,
    },
  };
};

export const markAsRead = async (
  conversation: ConversationModel
): Promise<[ConversationModel, number | undefined]> => {
  const token = conversation.data.token!;
  if (!token) {
    return [conversation, undefined];
  }

  const response = await sendMessageReadReceiptV3({
    conversation_token: token,
  });

  if (response.conversation) {
    return [
      updateConversation(conversation, response.conversation),
      response.total_unread_conversations,
    ];
  }

  return [conversation, response.total_unread_conversations];
};

export const setNeedsReply = (
  conversation: ConversationModel,
  needsReply: boolean
): ConversationModel => {
  return {
    ...conversation,
    data: {
      ...conversation.data,
      needs_reply: needsReply,
    },
    needsReply,
  };
};

export const useOpenConversation = (): ((
  conversation: ConversationModel
) => Promise<[ConversationModel, number | undefined]>) => {
  const firstFetchOrUpdateMessages = useFirstFetchOrUpdateMessages();

  return useCallback(
    async (conversation: ConversationModel) => {
      const updatedConversation =
        await firstFetchOrUpdateMessages(conversation);
      return markAsRead(updatedConversation);
    },
    [firstFetchOrUpdateMessages]
  );
};

export const setIsLoading = (
  conversation: ConversationModel,
  isLoading: boolean
): ConversationModel => {
  return {
    ...conversation,
    isLoading,
  };
};

export const useFetchMessages = (): ((
  conversation: ConversationModel,
  request?: Partial<INextRequest>,
  isMainStreamFetch?: boolean
) => Promise<ConversationModel>) => {
  const shouldUseListMessagesPage = useSettingEnableMessengerInlineContext();
  const { mutateAsync: listMessagesPage } = useListMessagesPageMutation();

  const fetchMessages = useCallback(
    async (
      conversation: ConversationModel,
      request: Partial<INextRequest> = {},
      isMainStreamFetch: boolean = false
    ) => {
      const token = conversation.data.token!;
      if (!token) {
        return conversation;
      }

      // FD-280135
      // This is a no-op right now, because we can't immediately return the
      // conversation that has `isLoading` set to true, and it gets set back to
      // false by the time the new conversation is actually returned.
      let updatedConversation = setIsLoading(conversation, true);

      try {
        if (shouldUseListMessagesPage) {
          const response = await listMessagesPage([
            IListMessagesPageRequest.build({
              conversation_token: token,
              query: request.query,
              max_results: request.max_results,
              pagination_tokens:
                IListMessagesPageRequest.IPaginationTokens.build({
                  ...request?.pagination_tokens,
                  standard_message: request.pagination_token,
                }),
            }),
          ]);
          updatedConversation = updateConversationFromFetchResponse(
            updatedConversation,
            isMainStreamFetch,
            response.messages
              .map((message) => message.standard_message)
              .filter(isNotUndefined),
            response.messages
              .map((message) => message.inline_message)
              .filter(isNotUndefined),
            response.message_authors,
            response.next_request
              ? {
                  ...IListMessagesRequestV3.build({
                    ...response.next_request,
                    pagination_token:
                      response.next_request?.pagination_tokens
                        ?.standard_message,
                  }),
                  pagination_tokens: response.next_request?.pagination_tokens,
                }
              : undefined
          );
        } else {
          const response = await getMessengerMessagesV3(
            IListMessagesRequestV3.build({
              ...request,
              conversation_token: token,
            })
          );
          updatedConversation = updateConversationFromFetchResponse(
            updatedConversation,
            isMainStreamFetch,
            response.messages,
            [],
            response.message_authors,
            response.next_request
          );
        }
      } catch (e) {
        // TODO: in the existing implementation fetch errors are ignored
        logError(e);
      } finally {
        updatedConversation = setIsLoading(updatedConversation, false);
      }

      return updatedConversation;
    },
    [listMessagesPage, shouldUseListMessagesPage]
  );

  return fetchMessages;
};

const updateConversationFromFetchResponse = (
  conversation: ConversationModel,
  isMainStreamFetch: boolean,
  messages: IMessengerMessage[],
  inlineMessages: IInlineMessage[],
  messageAuthors: IBasicUser[],
  nextRequest: INextRequest | undefined
): ConversationModel => {
  let updatedConversation = conversation;

  if (messages) {
    // Update message authors with uniqBy
    const updatedMessageAuthors = uniqBy(
      [...updatedConversation.messageAuthors, ...messageAuthors],
      (messageAuthor) => messageAuthor.token
    );

    // First update the message authors
    updatedConversation = {
      ...updatedConversation,
      messageAuthors: updatedMessageAuthors,
    };

    // Then create or update messages
    updatedConversation = createOrUpdateMessages(updatedConversation, messages);
  }

  if (inlineMessages) {
    updatedConversation = createOrUpdateInlineMessages(
      updatedConversation,
      inlineMessages
    );
  }

  if (isMainStreamFetch) {
    updatedConversation = {
      ...updatedConversation,
      nextRequest,
    };
  }

  return updatedConversation;
};

export const useFetchAdditionalMessagesThrottled = (): ((
  conversation: ConversationModel
) => () => Promise<ConversationModel>) => {
  const fetchMessages = useFetchMessages();

  return useCallback(
    (conversation: ConversationModel) => {
      return throttlePromise(async () => {
        if (conversation.nextRequest) {
          return fetchMessages(conversation, conversation.nextRequest, true);
        }
        return conversation;
      });
    },
    [fetchMessages]
  );
};

export const useFirstFetchOrUpdateMessages = (): ((
  conversation: ConversationModel
) => Promise<ConversationModel>) => {
  const fetchMessages = useFetchMessages();
  const fetchAdditionalMessagesThrottled =
    useFetchAdditionalMessagesThrottled();

  return useCallback(
    async (conversation: ConversationModel) => {
      if (conversation.isFirstFetch) {
        // Update isFirstFetch flag
        let updatedConversation = {
          ...conversation,
          isFirstFetch: false,
        };

        // Fetch messages for the first time
        updatedConversation = await fetchMessages(
          updatedConversation,
          undefined,
          true
        );

        // Then fetch additional messages immediately
        if (updatedConversation.nextRequest) {
          const fetchAdditionalMessagesThrottledCallback =
            fetchAdditionalMessagesThrottled(updatedConversation);
          updatedConversation =
            await fetchAdditionalMessagesThrottledCallback();
        }

        return updatedConversation;
      } else {
        // Just fetch messages without updating isFirstFetch
        return fetchMessages(conversation, { max_results: 50 });
      }
    },
    [fetchMessages, fetchAdditionalMessagesThrottled]
  );
};

export const createConversation = async (
  conversation: ConversationModel,
  messageContents: IMessageContents
): Promise<ConversationModel> => {
  if (!conversation.brand && !conversation.retailer) {
    return conversation;
  }

  const other_token = IMessengerParticipantToken.build({
    ...(conversation.user?.type === IUser.Type.BRAND_USER && {
      retailer_token: conversation.retailer?.token,
    }),
    ...(conversation.user?.type === IUser.Type.USER && {
      brand_token: conversation.brand?.token,
    }),
  });

  const response = await createConversationV3Action(
    ICreateConversationRequestV3.build({
      other_participant_token: other_token,
      message_contents: messageContents,
    })
  );

  if (!response.conversation) {
    logError(new Error("This request should return a new conversation"));
    return conversation;
  }

  return updateConversation(conversation, response.conversation);
};

export const sendNewMessage = async (
  conversation: ConversationModel,
  newMessage: Pick<IMessengerMessage, "text" | "images">
): Promise<ConversationModel> => {
  const message = createNewMessage(
    conversation.user.token!,
    newMessage,
    undefined
  );
  const updatedConversation = await sendMessage(message, conversation);

  // Create new messages map with the new message
  const updatedMessages = new Map(updatedConversation.messages);
  updatedMessages.set(getToken(message), message);

  return {
    ...updatedConversation,
    messages: updatedMessages,
    needsReply: false,
  };
};

export const uploadImagesAndSendNewMessage = async (
  conversation: ConversationModel,
  newMessage: Pick<IMessengerMessage, "text" | "images">,
  images?: readonly File[],
  products?: string[]
): Promise<ConversationModel> => {
  const message = createNewMessage(
    conversation.user.token!,
    newMessage,
    images,
    products
  );
  const updatedConversation = await sendMessage(message, conversation);

  // Create new messages map with the new message
  const updatedMessages = new Map(updatedConversation.messages);
  updatedMessages.set(getToken(message), message);

  return {
    ...updatedConversation,
    messages: updatedMessages,
    needsReply: false,
  };
};

export const insertConversationMessage = (
  conversation: ConversationModel,
  messageModel: MessageModel
): ConversationModel => {
  const updatedMessages = new Map(conversation.messages);
  updatedMessages.set(getToken(messageModel), messageModel);

  return {
    ...conversation,
    messages: updatedMessages,
  };
};

export const createOrUpdateMessages = (
  conversation: ConversationModel,
  messages: IMessengerMessage[]
): ConversationModel => {
  return messages.reduce(
    (updatedConversation, message) =>
      createOrUpdateMessage(updatedConversation, message),
    conversation
  );
};

export const createOrUpdateMessage = (
  conversation: ConversationModel,
  messageData: Partial<IMessengerMessage>
): ConversationModel => {
  if (!messageData.token) {
    throw new Error("message should have a token by now - Ian");
  }

  const updatedMessages = new Map(conversation.messages);
  const existingMessage = updatedMessages.get(messageData.token);

  if (existingMessage) {
    // Use the immutable update pattern
    const updatedMessage = updateMessage(
      existingMessage,
      messageData,
      undefined,
      messageData.product_tokens
    );
    updatedMessages.set(messageData.token, updatedMessage);
  } else {
    // Create a new message
    const newMessage = createNewMessage(
      messageData.author_token ?? "",
      messageData,
      undefined,
      messageData.product_tokens
    );
    updatedMessages.set(messageData.token, newMessage);
  }

  return {
    ...conversation,
    messages: updatedMessages,
  };
};

export const deleteMessage = async (
  conversation: ConversationModel,
  messageToken: string,
  strictLocalize: StrictLocalizeFunction
): Promise<ConversationModel> => {
  const message = conversation.messages.get(messageToken);
  if (!message) {
    return conversation;
  }

  const updatedMessages = new Map(conversation.messages);

  try {
    await deleteMessageApi(messageToken);
  } catch (e) {
    updatedMessages.set(
      messageToken,
      setError(message, getWebApiErrorMessage(e, strictLocalize))
    );
    return {
      ...conversation,
      messages: updatedMessages,
    };
  }

  // Update message after successful deletion
  updatedMessages.set(
    messageToken,
    updateMessage(
      message,
      {
        created_at: getCreatedAt(message),
        author_token: conversation.user.token!,
        deleted: true,
      },
      undefined
    )
  );

  return {
    ...conversation,
    messages: updatedMessages,
  };
};

export const createOrUpdateInlineMessages = (
  conversation: ConversationModel,
  messages: IInlineMessage[]
): ConversationModel => {
  return messages.reduce(
    (updatedConversation, message) =>
      createOrUpdateInlineMessage(updatedConversation, message),
    conversation
  );
};

export const createOrUpdateInlineMessage = (
  conversation: ConversationModel,
  inlineMessage: IInlineMessage
): ConversationModel => {
  if (!inlineMessage.token) {
    return conversation;
  }

  if (conversation.inlineMessages.has(inlineMessage.token)) {
    if (
      !isEqual(
        conversation.inlineMessages.get(inlineMessage.token),
        inlineMessage
      )
    ) {
      const inlineMessages = new Map(conversation.inlineMessages);
      inlineMessages.set(inlineMessage.token, inlineMessage);
      return {
        ...conversation,
        inlineMessages,
      };
    } else {
      return conversation;
    }
  } else {
    const inlineMessages = new Map(conversation.inlineMessages);
    inlineMessages.set(inlineMessage.token, inlineMessage);
    return {
      ...conversation,
      inlineMessages,
    };
  }
};

export const createSyntheticMessage = (
  conversation: ConversationModel,
  messageData: Partial<IMessengerMessage>
): ConversationModel => {
  return createOrUpdateMessage(conversation, {
    author_token: getConversationOtherPartyToken(conversation),
    read_at: Date.now(),
    ...messageData,
  });
};

export const createSyntheticBrandVacationMessage = (
  conversation: ConversationModel,
  strictLocalize: StrictLocalizeFunction
): ConversationModel => {
  // TEST(Ian): make sure this is the latest message
  if (conversation.brand && isOnVacation(conversation.brand)) {
    return createSyntheticMessage(conversation, {
      token: SYNTHETIC_BRAND_VACATION_TOKEN,
      text: strictLocalize(
        {
          id: "i6pPw/",
          defaultMessage: "We're on pause until {date}",
          description: {
            text: "Message indicating that the brand is on vacation and will return on a certain date",
          },
        },
        backwardsCompatibleValues({
          date: localDate(
            conversation.brand.vacation_end_date || new Date(),
            "PP"
          ),
        })
      ),
      created_at: conversation.brand.vacation_start_date,
      images: [],
    });
  }
  return conversation;
};

export const createSyntheticSupportMessage = (
  conversation: ConversationModel,
  type: MessengerConversationType | undefined,
  strictLocalize: StrictLocalizeFunction
): ConversationModel => {
  // TEST(Ian): make sure this is the oldest message
  if (type === "SUPPORT") {
    return createSyntheticMessage(conversation, {
      token: SYNTHETIC_SUPPORT_TOKEN,
      text: SYNTHETIC_SUPPORT_MESSAGE(strictLocalize),
      images: [],
    });
  } else if (conversation.messages.has(SYNTHETIC_SUPPORT_TOKEN)) {
    const updatedMessages = new Map(conversation.messages);
    updatedMessages.delete(SYNTHETIC_SUPPORT_TOKEN);

    return {
      ...conversation,
      messages: updatedMessages,
    };
  }

  return conversation;
};

export const createSyntheticStatusMessage = (
  conversation: ConversationModel,
  otherPartyStatus: string | undefined
): ConversationModel => {
  // TEST(Ian): make sure this is constant per day
  if (otherPartyStatus) {
    return createSyntheticMessage(conversation, {
      token: SYNTHETIC_OTHER_PARTY_STATUS_TOKEN,
      text: conversation.data.other_party_status,
      created_at: startOfDay(Date.now()).getTime(),
      images: [],
    });
  } else if (conversation.messages.has(SYNTHETIC_OTHER_PARTY_STATUS_TOKEN)) {
    const updatedMessages = new Map(conversation.messages);
    updatedMessages.delete(SYNTHETIC_OTHER_PARTY_STATUS_TOKEN);

    return {
      ...conversation,
      messages: updatedMessages,
    };
  }

  return conversation;
};

export const clearMessages = (
  conversation: ConversationModel
): ConversationModel => {
  return {
    ...conversation,
    messages: new Map(),
  };
};

// Standalone computed functions

// Simple property access examples
export const getConversationToken = (
  conversation: ConversationModel
): string => {
  return conversation.data.token!;
};

export const getConversationUserToken = (
  conversation: ConversationModel
): string => {
  return conversation.user.token!;
};

export const getConversationType = (
  conversation: ConversationModel
): MessengerConversationType => {
  return conversation.data.type as MessengerConversationType;
};

export const getConversationLastReadByCurrentUser = (
  conversation: ConversationModel
): number | undefined => {
  return conversation.data.last_read_at;
};

export const getConversationCreatedAt = (
  conversation: ConversationModel
): number | undefined => {
  return conversation.data.created_at;
};

export const getConversationOtherPartyPic = (
  conversation: ConversationModel
) => {
  if (
    conversation.otherParty &&
    isConversationOtherPartyBrandUser(conversation) &&
    conversation.brand &&
    conversation.brand.profile_image
  ) {
    return conversation.brand.profile_image;
  }

  return conversation.otherParty?.profile_image;
};

export const getConversationOtherPartyBusinessImage = (
  conversation: ConversationModel
) => {
  return conversation.data.other_party_business_image;
};

export const getConversationOtherPartyProfilePicUrl = (
  conversation: ConversationModel
) => {
  return getConversationOtherPartyPic(conversation)?.url;
};

export const isConversationCurrentUserARetailer = (
  conversation: ConversationModel
): boolean => {
  return conversation.user.roles.includes(UserRole.RETAILER);
};

export const isConversationCurrentUserABrand = (
  conversation: ConversationModel
): boolean => {
  return conversation.user.roles.includes(UserRole.MAKER);
};

export const getConversationUserProfilePhotoUrl = (
  conversation: ConversationModel,
  brand?: IGetMessengerConversationsResponse.ISimpleBrand
) => {
  const profileImage = conversation.user.profile_image;

  if (!profileImage) {
    return undefined;
  }

  if (
    isConversationCurrentUserBrandUser(conversation) &&
    brand &&
    brand.profile_image
  ) {
    return brand.profile_image.url;
  }

  return profileImage.url;
};

export const getConversationParticipants = (
  conversation: ConversationModel
): IMessengerConversationParticipant[] => {
  return conversation.data.participants;
};

export const getConversationLastModified = (
  conversation: ConversationModel
): number | undefined => {
  const latestMessage = getConversationLatestMessage(conversation);
  const latestMessageCreatedAt = latestMessage
    ? getCreatedAt(latestMessage)
    : undefined;
  const conversationCreatedAt = conversation.data.created_at;

  if (!latestMessageCreatedAt && !conversationCreatedAt) {
    return undefined;
  }

  if (!latestMessageCreatedAt) {
    return conversationCreatedAt;
  }

  if (!conversationCreatedAt) {
    return latestMessageCreatedAt;
  }

  return Math.max(latestMessageCreatedAt, conversationCreatedAt);
};

export const getConversationLatestMessage = (
  conversation: ConversationModel
): MessageModel | undefined => {
  return getConversationOrderedMessages(conversation).find(
    (message) => !isDeleted(message)
  );
};

export const getConversationLatestReadMessageFromCurrentUser = (
  conversation: ConversationModel
): MessageModel | undefined => {
  for (const message of getConversationOrderedMessages(conversation)) {
    if (
      isMessageFromCurrentUser(message, conversation) &&
      isMessageRead(message, conversation)
    ) {
      return message;
    }
  }
  return undefined;
};

export const getConversationOtherPartyToken = (
  conversation: ConversationModel
): string => {
  return conversation.otherParty?.token ?? "";
};

export const isConversationCurrentUserBrandUser = (
  conversation: ConversationModel
): boolean => {
  return conversation.user.type === IUser.Type.BRAND_USER;
};

export const isConversationOtherPartyBrandUser = (
  conversation: ConversationModel
): boolean => {
  return conversation.otherParty?.type === IUser.Type.BRAND_USER;
};

export const getConversationOtherPartyBrandToken = (
  conversation: ConversationModel
): string => {
  return isConversationOtherPartyBrandUser(conversation)
    ? (conversation.brand?.token ?? "")
    : "";
};

export const getConversationMessageCount = (
  conversation: ConversationModel
): number => {
  return conversation.messages.size;
};

export const isConversationSupportConversation = (
  conversation: ConversationModel
): boolean => {
  return conversation.data.type === MessengerConversationType.SUPPORT;
};

export const isConversationUnRead = (
  conversation: ConversationModel
): boolean => {
  return (conversation.data.unread_messages || 0) > 0;
};

export const isConversationStarredByBrand = (
  conversation: ConversationModel
): boolean => {
  return conversation.data.is_starred_by_brand ?? false;
};

export const getConversationOrderedMessages = (
  conversation: ConversationModel
): MessageModel[] => {
  return sortBy(Array.from(conversation.messages.values()), [
    (m: MessageModel) => {
      // Ensure we're not comparing undefined to numbers
      const createdAt = getCreatedAt(m);
      if (createdAt === undefined) {
        return 0; // Default value if createdAt is undefined
      }
      return -createdAt;
    },
  ]);
};

export const showConversationContactSupportMessage = (
  conversation: ConversationModel
): boolean => {
  return (
    !conversation.messages.size &&
    !conversation.isLoading &&
    !isConversationSupportConversation(conversation)
  );
};

export const isConversationMarkedAsSpam = (
  conversation: ConversationModel
): boolean => {
  return !!conversation.data.marked_as_spam;
};

export const isConversationInputDisabled = (
  conversation: ConversationModel
): boolean => {
  return isConversationDisabled(conversation);
};

export const isConversationDisabled = (
  conversation: ConversationModel
): boolean => {
  return (
    isConversationSupportConversation(conversation) &&
    !!conversation.data.other_party_status
  );
};

export const getConversationKey = (
  conversation: ConversationModel
): string | undefined => {
  return conversation.otherParty?.[
    conversation.user?.type === IUser.Type.BRAND_USER
      ? "retailer_token"
      : "brand_token"
  ];
};

export const getConversationIdentifier = (
  conversation: ConversationModel
): string | undefined => {
  if (conversation.data?.token !== undefined) {
    return conversation.data.token;
  }
  const brandOrRetailerToken = getConversationKey(conversation);
  if (brandOrRetailerToken !== undefined) {
    return brandOrRetailerToken;
  }
  return conversation.otherParty?.token;
};

export const getConversationOtherPartyUserType = (
  conversation: ConversationModel
): IUser.Type => {
  return IUser.Type[conversation.otherParty?.type ?? IUser.Type.UNKNOWN];
};

export const isMessageFromOtherUser = (
  message: MessageModel,
  conversation: ConversationModel
): boolean => {
  return message.authorToken === getConversationOtherPartyToken(conversation);
};

export const isMessageFromCurrentUser = (
  message: MessageModel,
  conversation: ConversationModel
): boolean => {
  return message.authorToken === getConversationUserToken(conversation);
};

export const isMessageFromSameRetailerAsCurrentUser = (
  message: MessageModel,
  conversation: ConversationModel,
  conversationMessageAuthors: IBasicUser[]
): boolean => {
  // Whether the message is from a retailer team that we are a part of.
  return (
    isConversationCurrentUserARetailer(conversation) &&
    !!conversationMessageAuthors
      .find((author: IBasicUser) => message.authorToken === author.token)
      ?.roles?.includes(UserRole.RETAILER)
  );
};

export const isMessageFromSameBrandAsCurrentUser = (
  message: MessageModel,
  conversation: ConversationModel,
  conversationMessageAuthors: IBasicUser[]
): boolean => {
  // Whether the message is from a brand team that we are a part of.
  const messageAuthor = conversationMessageAuthors.find(
    (author: IBasicUser) => message.authorToken === author.token
  );

  return (
    isConversationCurrentUserABrand(conversation) &&
    messageAuthor?.type === IUser.Type.BRAND_USER
  );
};

export const isMessageRead = (
  message: MessageModel,
  conversation: ConversationModel
): boolean => {
  if (message.data.read_at) {
    return true;
  }
  if (
    isMessageFromOtherUser(message, conversation) &&
    message.data.created_at &&
    getConversationLastReadByCurrentUser(conversation)
  ) {
    return (
      message.data.created_at <
      getConversationLastReadByCurrentUser(conversation)!
    );
  }
  return false;
};

export const isMessageLatestReadFromCurrentUser = (
  message: MessageModel,
  conversation: ConversationModel
): boolean => {
  return (
    message === getConversationLatestMessage(conversation) &&
    isMessageRead(message, conversation)
  );
};

export const isMessageLatestDelivered = (
  message: MessageModel,
  conversation: ConversationModel
): boolean => {
  return message === getConversationLatestMessage(conversation);
};

export const sendMessage = async (
  message: MessageModel,
  conversation: ConversationModel
): Promise<ConversationModel> => {
  let updatedConversation: IMessengerConversation | undefined;

  if (message.data.updated_at !== undefined) {
    throw new Error(
      "This should not be called if the message is already on the server - Ian"
    );
  }

  /*
  Messenger Service + Umbrella Brands Migration Setting
  */
  try {
    const imageTokens = await uploadImages(message);

    const messageContents = IMessageContents.build({
      text: message.data.text,
      image_tokens: imageTokens ?? [],
      product_tokens: message.productTokens ?? [],
    });

    if (!conversation.data.token) {
      updatedConversation = await createConversationV3Action(
        ICreateConversationRequestV3.build({
          other_participant_token: IMessengerParticipantToken.build({
            ...(conversation.user?.type === IUser.Type.BRAND_USER && {
              retailer_token: conversation.retailer?.token,
            }),
            ...(conversation.user?.type === IUser.Type.USER && {
              brand_token: conversation.brand?.token,
            }),
          }),
          message_contents: messageContents,
        })
      ).then((response) => {
        if (response.conversation) {
          updateConversation(conversation, response.conversation);
          return response.conversation;
        }
        throw new Error("This request should return a new conversation");
      });
    } else {
      const result = await sendMessengerMessageV3(
        ISendMessengerMessageRequestV3.build({
          conversation_token: conversation.data.token,
          message_contents: messageContents,
        })
      );

      updatedConversation = result.conversation;
    }

    if (!updatedConversation) {
      throw new Error(`This request should have returned a conversation.`);
    }

    message.data.token = updatedConversation.latest_message?.token;
    updateConversation(conversation, updatedConversation);
    return conversation;
  } catch (e) {
    logError(e);
    // Use the immutable pattern to update the message error
    const index = conversation.messages.get(getToken(message));
    if (index) {
      const updatedMessage = setError(
        message,
        e instanceof Error ? e.message : String(e)
      );
      const updatedMessages = new Map(conversation.messages);
      updatedMessages.set(getToken(message), updatedMessage);
      Object.assign(conversation, { messages: updatedMessages });
    }
    return conversation;
  }
};

export const getMessageOtherPartyFirstName = (
  message: MessageModel,
  conversation: ConversationModel
): string | undefined => {
  if (!isMessageFromOtherUser(message, conversation)) {
    return undefined;
  }

  return conversation.otherParty?.first_name || conversation.otherParty?.name;
};

export const getMessageOtherPartyProfilePicUrl = (
  message: MessageModel,
  conversation: ConversationModel
): string | undefined => {
  if (!isMessageFromOtherUser(message, conversation)) {
    return undefined;
  }

  return getConversationOtherPartyProfilePicUrl(conversation);
};

export const getMessageCurrentPartyProfilePicUrl = (
  conversation: ConversationModel,
  brand?: IGetMessengerConversationsResponse.ISimpleBrand
): string | undefined => {
  return getConversationUserProfilePhotoUrl(conversation, brand);
};
